import { endSession, getAccessToken } from './session';

export const BASE_URL =
  import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:5143/api';

export async function apiFetch<T>(
  endpoint: string,
  options?: RequestInit
): Promise<T> {
  const url = `${BASE_URL}${endpoint}`;
  const token = getAccessToken();
  const response = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options?.headers,
    },
  });

  // A signed-in request that comes back 401 means the session expired or was
  // revoked: send the user back to sign in. (Sign-in itself has no token yet,
  // so its 401s fall through and are shown on the page.)
  if (response.status === 401 && token) {
    endSession();
    throw new Error('Your session has expired. Please sign in again.');
  }

  if (!response.ok) {
    let errorMsg = `API Error: ${response.status} ${response.statusText}`;
    try {
      // Two shapes come back: `{ error }` from the exception middleware, and a
      // FluentValidation failure list from model binding.
      const data: unknown = await response.json();
      if (Array.isArray(data)) {
        errorMsg = data
          .map((failure) => (failure as { errorMessage?: string }).errorMessage)
          .filter(Boolean)
          .join(', ');
      } else if (data && typeof data === 'object' && 'error' in data) {
        errorMsg = String((data as { error: unknown }).error);
      }
    } catch {
      // Ignore if body is not JSON
    }
    throw new Error(errorMsg);
  }

  if (response.status === 204) {
    return {} as T;
  }

  return response.json() as Promise<T>;
}
